import React, { useEffect, useState } from 'react';
import { washHub } from 'store/slices';
import { useWashHubListener } from 'hooks';
import { QALogProps } from './QALog.props';
import QALogView from './QALog.view';
import { fetchQALog } from 'api/QALog';
import useUrlSharing from 'hooks/useUrlSharing';
import useQueryParams from 'hooks/useQueryParams';
import { useQuery } from 'react-query';

const QALogContainer = (_: QALogProps) => {
  const queryUrl = useQueryParams();

  const [page, setPage] = useState<number>(1);
  const [endDate, setEndDate] = useState<Date>();
  const [startDate, setStartDate] = useState<Date>();
  const [employees, setEmployees] = useState<string[]>();
  const [roles, setRoles] = useState<string[]>();
  const [urlRecord, setUrlRecord] = useState({});
  const [isUrlMapped, setIsUrlMapped] = useState<boolean>(false);
  const washHubId = washHub.selectors.useSelectedWashHubId();
  const isWashHubIdValid = washHub.selectors.useIsWashHubIdValid();
  const [sortBy, setSortBy] = useState<number | undefined>(undefined);

  const queryKey = [
    'fetch-qa-log',
    page,
    startDate,
    endDate,
    employees,
    roles,
    washHubId,
    sortBy,
  ];

  const isUpdated = useUrlSharing(urlRecord, washHubId);

  const queryFn = () =>
    fetchQALog({
      page,
      limit: 10,
      washHub: washHubId,
      employees,
      startDate,
      endDate,
      roles,
      sort: sortBy,
    });

  const options = {
    enabled: isWashHubIdValid,
    keepPreviousData: true,
  };

  const { data, isLoading, isFetching } = useQuery(queryKey, queryFn, options);

  useWashHubListener(() => {
    setPage(1);
  });

  const qaLogs = data?.items || [];
  const totalCount = data?.total || 0;
  const totalPages = data?.totalPages || 0;

  useEffect(() => {
    const urlParams = {
      washHubId: String(washHubId),
      ...(startDate ? { startDate: `${startDate.valueOf()}` } : {}),
      ...(endDate ? { endDate: `${endDate.valueOf()}` } : {}),
      ...(employees ? { employees: employees.join('-') } : {}),
      ...(roles ? { roles: roles.join('-') } : {}),
    };
    setUrlRecord(urlParams);
  }, [washHubId, endDate, startDate, employees, roles]);

  useEffect(() => {
    const { employees, startDate, endDate, roles } = queryUrl;
    employees && setEmployees(employees.split('-'));
    roles && setRoles(roles.split('-'));
    startDate && setStartDate(new Date(+startDate));
    endDate && setEndDate(new Date(+endDate));
    if (isUpdated) setIsUrlMapped(true);
  }, [isUpdated]);

  useWashHubListener(() => {
    setStartDate(undefined);
    setEndDate(undefined);
    setEmployees(undefined);
    setRoles(undefined);

    onClearFilters();
  });

  const onClearFilters = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    setEmployees(undefined);
    setRoles(undefined);
  };

  const onSearchPress = (startDate: Date | null, endDate: Date | null) => {
    if (startDate) {
      setStartDate(startDate);
    }

    if (endDate) {
      setEndDate(endDate);
    }
  };

  const onSearchClear = () => {
    setStartDate(undefined);
    setEndDate(undefined);
  };

  const onApplyFilters = (data: string[], type: string) => {
    setPage(1);

    if (type === 'employee') setEmployees(data);
    if (type === 'roles') setRoles(data);
  };

  const pagination = {
    page,
    setPage,
    totalCount,
    totalPages,
  };

  const _props = {
    qaLogs,
    pagination,
    isLoading: isLoading || isFetching,
    onClearFilters,
    startDate,
    endDate,
    roles,
    employees,
    onApplyFilters,
    onSearchPress,
    onSearchClear,
    setSortBy,
  };

  return <>{isUrlMapped && <QALogView {..._props} />}</>;
};

export default QALogContainer;
